/**
 * 懒加载分包（chunk）加载失败的自愈。
 *
 * ── 为什么需要 ───────────────────────────────────────────────────────────
 * 重新部署后，旧的 HTML（在某个还开着的页签里）会去请求**已被删除的旧哈希 chunk**：
 *   · nginx 的 `try_files $uri =404` 让它 404（若哪套配置漏了 =404，还会被 SPA 兜底
 *     成 `200 text/html` —— 浏览器按 MIME 不符直接丢弃）；
 *   · 表现是「点了某个菜单/按钮后白屏或整页报错」，必须手动 F5 才恢复。
 *
 * 本仓原有的兜底只覆盖**首屏**：`public/scripts/loading.js` 的 `onResourceError`
 * 在 `bootPending` 为假时立即 return，React 起来后（`app.tsx` 的
 * `finishBootPageLoading()`）监听即被摘除 ⇒ 运行期 chunk 404 无人管。
 * `src/components/ErrorBoundary` 只认渲染异常，不认「chunk 加载失败」。
 *
 * ── 本模块做什么 ────────────────────────────────────────────────────────
 * 全局监听两类信号，命中后**一次性** reload（护栏见 `./reloadGuard`）：
 *   ① `unhandledrejection`：`import()` / React.lazy 失败（拿不到 chunk）；
 *   ② `error`（capture）：chunk `<script>` / `<link>` 自身加载失败。
 * 不满足安全刷新条件（编辑态/弹窗/上传中/隐藏标签页/PDA）时**降级为提示**，
 * 由用户自己点「刷新页面」—— 绝不为了自愈去丢用户的未保存内容。
 *
 * ── 刻意不接管的地方 ───────────────────────────────────────────────────
 * `src/pages/modules/sales/quotation/LazyQuotationEmailPdfAttachment.tsx` 自己
 * `import().then(ok, err)` 捕掉失败并保住草稿（不 reload）。它的失败不会变成
 * unhandledrejection，本模块天然放行；请勿在此处按 URL 黑名单去拦它。
 */

import { Button, notification } from 'antd';
import React from 'react';
import {
  hardReload,
  installReloadGuardApi,
  requestAutoReload,
  type AutoReloadBlockReason,
} from './reloadGuard';

/** 在同一会话里为 chunk 自愈保留的自动刷新额度（见 reloadGuard 的 MAX 上限） */
const CHUNK_RELOAD_SCOPE = 'chunk';
const NOTICE_KEY = 'dingyan-chunk-recovery';
/** 同一批失败会连发多条事件（多个 chunk 同时 404），合并到一次处理 */
const DEDUPE_WINDOW_MS = 800;

/**
 * chunk 失败的文本判据。
 * 需要同时覆盖：webpack（`Loading chunk N failed` / `ChunkLoadError`）、
 * 浏览器原生动态 `import()`（Chrome/Edge 的 `Failed to fetch dynamically imported module`、
 * Firefox 的 `error loading dynamically imported module`、Safari 的
 * `Importing a module script failed`）以及 mako 的异步分包。
 */
const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /Loading chunk [\w-]+ failed/i,
  /Loading CSS chunk [\w-]+ failed/i,
  /\bChunkLoadError\b/,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
];

/** 带内容哈希的构建产物：`umi.<hash>.js` / `umi.<hash>.css` / `umi.<hash>.js.map` 不算 */
const HASHED_ASSET_PATTERN = /\.[0-9a-f]{6,}\.(?:js|css)$/i;
/** webpack/mako 的非入口分包：`p__xxx.async.js`、`xxx.chunk.js`、`xxx.async.css` */
const ASYNC_ASSET_PATTERN = /\.(?:async|chunk)\.(?:js|css)$/i;

/** 事件对象可能包着 cause / reason，逐层取文本，不抛错 */
function collectErrorText(error: unknown, depth = 0): string {
  if (error == null || depth > 3) return '';
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return String(error);
  const record = error as { message?: unknown; name?: unknown; cause?: unknown; reason?: unknown };
  const parts: string[] = [];
  if (typeof record.name === 'string') parts.push(record.name);
  if (typeof record.message === 'string') parts.push(record.message);
  parts.push(collectErrorText(record.cause, depth + 1));
  parts.push(collectErrorText(record.reason, depth + 1));
  return parts.join(' ');
}

/** 判定任意错误是否为「chunk 加载失败」。绝不抛错，不认识就当 false。 */
export function isChunkLoadError(error: unknown): boolean {
  const text = collectErrorText(error);
  if (!text) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/** 判定资源加载失败事件是否指向「本站的构建产物」 */
function isFailedBuildAsset(target: unknown): boolean {
  const el = target as { tagName?: string; src?: string; href?: string } | null;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toUpperCase();
  if (tag !== 'SCRIPT' && tag !== 'LINK') return false;
  const raw = el.src || el.href;
  if (!raw || typeof window === 'undefined') return false;
  let url: URL;
  try {
    url = new URL(raw, window.location.href);
  } catch {
    return false;
  }
  // 只认本站资源：第三方 CDN 的抖动不该触发整页刷新
  if (url.origin !== window.location.origin) return false;
  const path = url.pathname;
  // 无哈希、非 async/chunk 命名的脚本（如 print-lock.css、第三方固定名脚本）不认：
  // 它们失败与「部署换版本」无关，重载解决不了，只会误伤
  return HASHED_ASSET_PATTERN.test(path) || ASYNC_ASSET_PATTERN.test(path);
}

/** 降级提示：本轮不能自动刷新（编辑态等），把决定权交给用户 */
function notifyManualRefresh(reason: AutoReloadBlockReason | 'quota'): void {
  const editing = reason === 'editing' || reason === 'unsaved' || reason === 'editable-control';
  notification.open({
    key: NOTICE_KEY,
    message: '页面资源加载失败',
    description: editing
      ? '本站发布了新版本，当前页面引用的旧资源已失效。检测到可能有未保存的内容，因此没有自动刷新 —— 请先保存，再点「刷新页面」。'
      : '本站发布了新版本，当前页面引用的旧资源已失效，需要刷新后继续。',
    duration: 0,
    btn: React.createElement(
      Button,
      {
        type: 'primary',
        size: 'small',
        onClick: () => {
          notification.destroy(NOTICE_KEY);
          // 用户显式要求刷新：走 hardReload，顺带清掉可能残留的 SW / Cache Storage
          hardReload();
        },
      },
      '刷新页面',
    ),
  });
}

/** 命中 chunk 失败后的统一处置：护栏通过就自动刷新，否则提示 */
export function handleChunkLoadFailure(source: string): void {
  if (typeof window === 'undefined') return;
  console.warn(`[chunkRecovery] 检测到分包加载失败（${source}），尝试自愈`);
  const blockReason = requestAutoReload(CHUNK_RELOAD_SCOPE);
  if (blockReason) notifyManualRefresh(blockReason === 'already-reloaded' ? 'quota' : blockReason);
}

let installed = false;
let lastHandledAt = 0;

/**
 * 装配全局监听（幂等）。由 `src/app.tsx` 在应用启动时调用一次。
 */
export function installChunkRecovery(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // 业务页可通过 window.__dingyanReloadGuard.markUnsaved(true) 参与护栏
  installReloadGuardApi();

  const withinDedupeWindow = () => {
    const now = Date.now();
    if (now - lastHandledAt < DEDUPE_WINDOW_MS) return true;
    lastHandledAt = now;
    return false;
  };

  // ① 动态 import / React.lazy 的失败：rejection 里带着 chunk 错误
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) return;
    if (withinDedupeWindow()) return;
    handleChunkLoadFailure('unhandledrejection');
  });

  // ② chunk 的 <script> / <link> 标签自身加载失败（capture 才能拿到资源事件）
  window.addEventListener(
    'error',
    (event: Event) => {
      const target = event.target;
      // 运行期 JS 异常同样会走到 window error，但它由 ErrorBoundary 负责，这里只认资源事件
      if (!target || target === window) return;
      if (!isFailedBuildAsset(target)) return;
      if (withinDedupeWindow()) return;
      handleChunkLoadFailure('resource-error');
    },
    true,
  );
}